import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useAccountStore } from '../account/account.state';
import { useFormStore } from '../form/formStore';
import { RequestRoadsideAssistanceDetailsPersonal } from '@/interfaces/pages/request-roadside-assistance.interface';
import { RoadsideAssistanceService as _roadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { RequestRoadsideAssistanceService as _requestRoadsideAssistanceService } from '@/services/store/request-roadside-assistance.service';
import {
  GetMemberContactDetailsRequest,
  GetMemberContactDetailsResponse,
  ContactMethod,
  SendMembershipCardNumberRequest,
  SendMembershipCardNumberResponse,
  GetMemberContactMethod,
} from '@/interfaces/entities/roadside-assistance.interface';
import {
  YOUR_DETAILS_VEHICLE_DETAILS_STEP_ID,
  createRsaJobHtmlRequest,
} from '@/forms/request-roadside-assistance/mappings/request-roadside-assistance-mappings';
import { useRoadsideAssistanceBase } from './request-roadside-assistance';
import { IReviewSummary, IReviewSummaryItem } from '@/interfaces';

export const useRequestRoadsideAssistancePersonalStore = defineStore('RequestRoadsideAssistance', () => {
  const accountStore = useAccountStore();
  const formStore = useFormStore();

  // State
  const requestRoadsideAssistanceDetails = ref<RequestRoadsideAssistanceDetailsPersonal>({
    membershipCardNumber: '',
    phoneNumber: '',
    registrationNumber: '',
    breakdownLocationLatitude: 0,
    breakdownLocationLongitude: 0,
    breakdownLocationAddress: '',
    breakdownCause: '',
    comments: '',
    vehicle: null,
    isCovered: false,
    isCoveredForAnyVehicle: false,
    sendMembershipModel: null,
    contactMethods: null,
  });

  // Inherit functions from the base roadsideassistance
  const {
    setBreakdownLocation,
    setComments,
    clearComments,
    clearBreakdownCause,
    isVehicleDetailsFromPortfolio,
    rsaTrackingId,
    dispatchGroup,
    setVehicleDetails,
    checkAndAdjustStep,
    isTrackable,
  } = useRoadsideAssistanceBase(requestRoadsideAssistanceDetails.value);

  const isMemberLoggedIn = ref<boolean>(false);
  const loggeindInMembershipCardNumber = ref<string | null>(null);
  const isPersonalDetailsStepDirty = ref(false);

  const buildYear = computed(() => requestRoadsideAssistanceDetails.value.vehicle?.buildYear ?? 0);
  const make = computed(() => requestRoadsideAssistanceDetails.value.vehicle?.make ?? '');
  const model = computed(() => requestRoadsideAssistanceDetails.value.vehicle?.model ?? '');
  const colour = computed(() => requestRoadsideAssistanceDetails.value.vehicle?.colour ?? '');
  const isMissingVehicleDetails = computed(() => !hasAllVehicleDetails());

  const reviewSummaryItemEdit = ref<IReviewSummaryItem>();
  const reviewSummaryEdit = ref<IReviewSummary>();

  // Actions

  function hasAllVehicleDetails() {
    return buildYear.value > 0 && !!make.value.trim() && !!model.value.trim() && !!colour.value.trim();
  }

  function setPersonalDetails(membershipCardNumber: string, phoneNumber: string, registrationNumber: string) {
    requestRoadsideAssistanceDetails.value.membershipCardNumber = membershipCardNumber;
    requestRoadsideAssistanceDetails.value.phoneNumber = phoneNumber;
    requestRoadsideAssistanceDetails.value.registrationNumber = registrationNumber;
  }

  function setReviewSummaryEditClicked(summary: IReviewSummary, summaryReviewItem: IReviewSummaryItem) {
    reviewSummaryEdit.value = summary;
    reviewSummaryItemEdit.value = summaryReviewItem;
  }

  async function checkAndSetRsaEligibility(): Promise<boolean> {
    try {
      if (
        !requestRoadsideAssistanceDetails.value.registrationNumber ||
        !requestRoadsideAssistanceDetails.value.membershipCardNumber
      ) {
        console.error('Missing Vehicle Rego or Membership Card Number');
        throw new Error('Missing Vehicle Rego or Membership Card Number');
      }

      const response = await _roadsideAssistanceService.checkEligibility(
        requestRoadsideAssistanceDetails.value.registrationNumber,
        null,
        requestRoadsideAssistanceDetails.value.membershipCardNumber
      );
      if (response) {
        isPersonalDetailsStepDirty.value = false;
        requestRoadsideAssistanceDetails.value.isCovered = response.isCovered;
        requestRoadsideAssistanceDetails.value.isCoveredForAnyVehicle =
          !!response.policyEligibleForAnyVehicles || response.loyaltyLevelEligibleForAnyVehicles;

        requestRoadsideAssistanceDetails.value.vehicle = response.policyVehicle;
        isVehicleDetailsFromPortfolio.value = !!response.policyVehicle;
      } else {
        requestRoadsideAssistanceDetails.value.isCovered = false;
        requestRoadsideAssistanceDetails.value.isCoveredForAnyVehicle = false;
        requestRoadsideAssistanceDetails.value.vehicle = null;
        isVehicleDetailsFromPortfolio.value = false;
      }

      return response != null;
    } catch {
      return false;
    }
  }

  async function setMembershipFromIdentity(): Promise<boolean> {
    try {
      // Get identity details and prefill user details
      const identity = accountStore.identityDetails;

      // Fill in user details from identity
      if (identity !== undefined) {
        if (identity.membershipCardNumber) loggeindInMembershipCardNumber.value = identity.membershipCardNumber!;
      }

      isMemberLoggedIn.value = !!identity?.d365Id;
      return true;
    } catch {
      return false;
    }
  }

  function setSendMembershipStep1Details(lName: string, dob: string, rego: string) {
    requestRoadsideAssistanceDetails.value.sendMembershipModel = {
      lastName: lName,
      dateOfBirth: dob,
      nominatedVehicleRegistrationNumber: rego,
      contactMethodId: '',
    };
  }

  function setSendMembershipStep2Details(contactId: string) {
    if (requestRoadsideAssistanceDetails.value.sendMembershipModel) {
      requestRoadsideAssistanceDetails.value.sendMembershipModel.contactMethodId = contactId;
    }
  }

  function setContactMethods(contactMethods: GetMemberContactMethod[]) {
    requestRoadsideAssistanceDetails.value.contactMethods = contactMethods;
  }

  function $resetSendMembershipModel() {
    requestRoadsideAssistanceDetails.value.sendMembershipModel = null;
    requestRoadsideAssistanceDetails.value.contactMethods = null;
  }

  async function getMemberContactDetails(): Promise<GetMemberContactDetailsResponse | null> {
    if (!requestRoadsideAssistanceDetails.value.sendMembershipModel) {
      return Promise.resolve(null);
    }

    const request: GetMemberContactDetailsRequest = {
      lastName: requestRoadsideAssistanceDetails.value.sendMembershipModel.lastName,
      registrationNumber: requestRoadsideAssistanceDetails.value.sendMembershipModel.nominatedVehicleRegistrationNumber,
      dateOfBirth: requestRoadsideAssistanceDetails.value.sendMembershipModel.dateOfBirth,
    };
    return await _requestRoadsideAssistanceService.getMemberContactDetails(request);
  }

  async function sendMembershipCardNumber(): Promise<SendMembershipCardNumberResponse | null> {
    if (!requestRoadsideAssistanceDetails.value.sendMembershipModel) {
      return Promise.resolve(null);
    }

    const request: SendMembershipCardNumberRequest = {
      contactMethod: {
        id: requestRoadsideAssistanceDetails.value.sendMembershipModel.contactMethodId,
      } as ContactMethod,
      lastName: requestRoadsideAssistanceDetails.value.sendMembershipModel.lastName,
      registrationNumber: requestRoadsideAssistanceDetails.value.sendMembershipModel.nominatedVehicleRegistrationNumber,
      dateOfBirth: requestRoadsideAssistanceDetails.value.sendMembershipModel.dateOfBirth,
    };
    return await _requestRoadsideAssistanceService.sendMembershipCardNumber(request);
  }

  const createRsaJobHtml = async () => {
    try {
      const rsaRequest = await createRsaJobHtmlRequest(requestRoadsideAssistanceDetails.value);
      const result = await _roadsideAssistanceService.createRsaJobHtml(rsaRequest);

      if (result?.jobId) {
        // Set the trackingId if successful
        rsaTrackingId.value = result.jobId;
        dispatchGroup.value = result.roadsideAssistanceStatus?.dispatchGroup;
      }
      return result;
    } catch {
      return null;
    }
  };

  /*
   * Add/ remove steps
   */
  const adjustSteps = async () => {
    if (
      requestRoadsideAssistanceDetails.value.isCovered &&
      (isMissingVehicleDetails.value || !isVehicleDetailsFromPortfolio.value)
    ) {
      // Add vehicle step
      await checkAndAdjustStep(YOUR_DETAILS_VEHICLE_DETAILS_STEP_ID, true, formStore.currentStepIndex + 1);
    } else {
      // Remove vehicle step
      await checkAndAdjustStep(YOUR_DETAILS_VEHICLE_DETAILS_STEP_ID);
    }
  };

  return {
    requestRoadsideAssistanceDetails,
    isMemberLoggedIn,
    loggeindInMembershipCardNumber,
    isPersonalDetailsStepDirty,
    isVehicleDetailsFromPortfolio,
    reviewSummaryEdit,
    reviewSummaryItemEdit,
    rsaTrackingId,
    dispatchGroup,
    setBreakdownLocation,
    setComments,
    clearComments,
    clearBreakdownCause,
    setPersonalDetails,
    setVehicleDetails,
    setReviewSummaryEditClicked,
    checkAndSetRsaEligibility,
    setMembershipFromIdentity,
    adjustSteps,
    createRsaJobHtml,
    isTrackable,
    setSendMembershipStep1Details,
    setSendMembershipStep2Details,
    setContactMethods,
    $resetSendMembershipModel,
    getMemberContactDetails,
    sendMembershipCardNumber,
  };
});
